/**
 * GpuTimer — GPU time of the render, per frame, via EXT_disjoint_timer_query_webgl2.
 *
 * The overlay's `render` figure is CPU submit time; GL is asynchronous, so a GPU-bound frame shows
 * as a SHORT render and a long frame interval, and a 60 Hz vsync then quantises fps to 60/30/20.
 * This is the only number that says what the GPU actually spent. One TIME_ELAPSED query brackets
 * the frame's draws; results are read back a few frames later without stalling, and a frame whose
 * timer was disjoint (clock change, context switch) is dropped rather than averaged in.
 *
 * `ms` is 0 when the extension is unavailable (Firefox, Safari, some Android).
 */
import type * as THREE from 'three';

const AVG_WINDOW = 60;
const MAX_PENDING = 8;

interface TimerExt {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
}

export class GpuTimer {
  readonly available: boolean;
  /** Rolling-average GPU ms per frame over the last AVG_WINDOW completed queries. */
  ms = 0;

  private gl: WebGL2RenderingContext | null = null;
  private ext: TimerExt | null = null;
  private active: WebGLQuery | null = null;
  private pending: WebGLQuery[] = [];
  private free: WebGLQuery[] = [];
  private samples: number[] = [];

  constructor(renderer: THREE.WebGLRenderer) {
    const gl = renderer.getContext();
    const isGl2 = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;
    const ext = isGl2 ? (gl.getExtension('EXT_disjoint_timer_query_webgl2') as TimerExt | null) : null;
    this.available = ext !== null;
    if (isGl2 && ext) {
      this.gl = gl;
      this.ext = ext;
    }
  }

  /** Start timing; a no-op while a query is open or the read-back queue is full. */
  begin(): void {
    if (!this.gl || !this.ext || this.active || this.pending.length >= MAX_PENDING) return;
    const q = this.free.pop() ?? this.gl.createQuery();
    if (!q) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
    this.active = q;
  }

  end(): void {
    if (!this.gl || !this.ext || !this.active) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.pending.push(this.active);
    this.active = null;
  }

  /** Read back finished queries (in order) and refresh `ms`. Call once per frame, after end(). */
  poll(): void {
    const gl = this.gl, ext = this.ext;
    if (!gl || !ext || this.pending.length === 0) return;
    // Reading GPU_DISJOINT resets it; read once per poll and apply to whatever completes now.
    const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT) as boolean;
    while (this.pending.length > 0) {
      const q = this.pending[0];
      if (!gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) break; // results complete in order
      this.pending.shift();
      if (!disjoint) {
        const ns = gl.getQueryParameter(q, gl.QUERY_RESULT) as number;
        this.samples.push(ns / 1e6);
        if (this.samples.length > AVG_WINDOW) this.samples.shift();
      }
      this.free.push(q);
    }
    if (this.samples.length > 0) {
      let sum = 0;
      for (let i = 0; i < this.samples.length; i++) sum += this.samples[i];
      this.ms = sum / this.samples.length;
    }
  }

  dispose(): void {
    const gl = this.gl;
    if (!gl) return;
    for (const q of [...this.pending, ...this.free]) gl.deleteQuery(q);
    if (this.active) gl.deleteQuery(this.active);
    this.pending = []; this.free = []; this.active = null;
  }
}
